import React, { useState } from 'react';
import { CheckCircle, Circle, ArrowRight, User, FileText, Wallet, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DemoWalkthrough = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: 'Customer Submits Lead',
      description: 'A homeowner visits the website and fills out the quote request form.',
      details: [
        'Customer enters name, email, phone',
        'Selects service needed (AC Repair, Installation, etc.)',
        'Clicks "Get My Free Quote"',
        'Lead is instantly saved to database',
        'You receive an email alert (if configured)'
      ],
      demo: {
        type: 'form',
        data: {
          name: 'John Smith',
          email: 'john.smith@email.com', 
          phone: '(504) 555-1234',
          service: 'AC Repair & Service'
        }
      }
    },
    {
      title: 'Lead Appears in Contractor Portal',
      description: 'Registered contractors see the new lead in their dashboard.',
      details: [
        'Lead shows customer name and service type',
        'Contact info is HIDDEN until claimed',
        'Price to claim is displayed ($25-$85)',
        'Multiple contractors see the same lead',
        'First to claim gets exclusive access'
      ],
      demo: {
        type: 'lead',
        data: {
          name: 'John Smith',
          service: 'AC Repair & Service',
          time: '2 minutes ago',
          price: '$35'
        }
      }
    },
    {
      title: 'Contractor Adds Funds',
      description: 'Contractor must have balance to claim leads.',
      details: [
        'Click "Add Funds" button',
        'Choose package: $100, $250, $500, $1,000',
        'Pay securely via Stripe',
        'Balance added instantly',
        'Funds never expire'
      ],
      demo: {
        type: 'packages',
        data: [
          { label: '$100', leads: '~3 leads' },
          { label: '$250', leads: '~7 leads' },
          { label: '$500', leads: '~14 leads' },
          { label: '$1,000', leads: '~28 leads' }
        ]
      }
    },
    {
      title: 'Contractor Claims Lead',
      description: 'Click "Claim This Lead" to get exclusive access.',
      details: [
        'Lead cost deducted from balance',
        'Lead is now YOURS exclusively',
        'Other contractors can no longer see it',
        'Full contact info is revealed',
        'Call customer within 5 minutes for best results!'
      ],
      demo: {
        type: 'claimed',
        data: {
          name: 'John Smith',
          phone: '(504) 555-1234',
          email: 'john.smith@email.com',
          service: 'AC Repair & Service'
        }
      }
    },
    {
      title: 'Contact & Close the Deal',
      description: 'Reach out to the customer and win the job!',
      details: [
        'Call or text the customer immediately',
        'Mention they requested HVAC service online',
        'Schedule an appointment',
        'Provide great service',
        'Grow your business!'
      ],
      demo: {
        type: 'success'
      }
    }
  ];

  const currentStepData = steps[currentStep];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0F172A] text-white py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0EA5E9] rounded-xl flex items-center justify-center">
              <span className="font-bold text-xl">N</span>
            </div>
            <div>
              <span className="text-xl font-bold">NOLA HVAC Connect</span>
              <p className="text-xs text-slate-400">Contractor Demo Walkthrough</p>
            </div>
          </div>
          <a href="/contractors/portal" className="bg-[#F97316] px-4 py-2 rounded-full text-sm font-bold hover:bg-[#EA580C]">
            Go to Contractor Portal →
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-12 overflow-x-auto pb-4">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center">
              <button
                onClick={() => setCurrentStep(index)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                  index === currentStep
                    ? 'bg-[#0F172A] text-white'
                    : index < currentStep
                    ? 'bg-green-100 text-green-800'
                    : 'bg-slate-100 text-slate-500'
                }`}
              >
                {index < currentStep ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </span>
                )}
                <span className="text-sm font-medium hidden md:inline">{step.title}</span>
              </button>
              {index < steps.length - 1 && (
                <ArrowRight className="w-4 h-4 text-slate-300 mx-2 hidden md:block" />
              )}
            </div>
          ))}
        </div>

        {/* Current Step Content */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left - Info */}
          <div className="bg-white p-8 rounded-2xl border border-slate-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-xl flex items-center justify-center">
                <span className="text-[#0EA5E9] font-bold text-xl">{currentStep + 1}</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {currentStepData.title}
                </h2>
                <p className="text-slate-500">{currentStepData.description}</p>
              </div>
            </div>

            <ul className="space-y-3">
              {currentStepData.details.map((detail) => (
                <li key={detail} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">{detail}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex gap-3">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
              >
                Previous
              </Button>
              <Button
                onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
                disabled={currentStep === steps.length - 1}
                className="bg-[#F97316] hover:bg-[#EA580C]"
              >
                Next Step
              </Button>
            </div>
          </div>

          {/* Right - Demo Visual */}
          <div className="bg-white p-8 rounded-2xl border border-slate-200">
            <p className="text-sm text-slate-500 uppercase tracking-wide mb-4 font-medium">Preview</p>
            
            {currentStepData.demo.type === 'form' && (
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <h3 className="font-bold text-[#0F172A] mb-4">Quote Request Form</h3>
                <div className="space-y-3">
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">Full Name</p>
                    <p className="font-medium">{currentStepData.demo.data.name}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium">{currentStepData.demo.data.email}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">Phone</p>
                    <p className="font-medium">{currentStepData.demo.data.phone}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500">Service Needed</p>
                    <p className="font-medium">{currentStepData.demo.data.service}</p>
                  </div>
                  <div className="bg-[#F97316] text-white text-center py-3 rounded-full font-bold">
                    Get My Free Quote
                  </div>
                </div>
              </div>
            )}

            {currentStepData.demo.type === 'lead' && (
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <h3 className="font-bold text-[#0F172A] mb-4">Available Lead</h3>
                <div className="bg-white p-4 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-semibold">{currentStepData.demo.data.name}</p>
                      <p className="text-xs text-slate-500">{currentStepData.demo.data.time}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="bg-[#0EA5E9]/10 text-[#0EA5E9] px-3 py-1 rounded-full text-sm">
                      {currentStepData.demo.data.service}
                    </span>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#F97316]">{currentStepData.demo.data.price}</p>
                      <p className="text-xs text-slate-400">to claim</p>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                    <p className="text-red-600 text-sm">📞 Contact info hidden until claimed</p>
                  </div>
                </div>
              </div>
            )}

            {currentStepData.demo.type === 'packages' && (
              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <h3 className="font-bold text-[#0F172A] mb-4">Add Funds</h3>
                <div className="grid grid-cols-2 gap-3">
                  {currentStepData.demo.data.map((pkg) => (
                    <div 
                      key={pkg.label}
                      className={`p-4 rounded-xl border-2 text-center ${
                        pkg.label === '$250' ? 'border-[#F97316] bg-orange-50' : 'border-slate-200 bg-white'
                      }`}
                    >
                      <p className="text-2xl font-bold text-[#0F172A]">{pkg.label}</p>
                      <p className="text-xs text-slate-500">{pkg.leads}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 bg-[#F97316] text-white text-center py-3 rounded-full font-bold">
                  Pay $250
                </div>
              </div>
            )}

            {currentStepData.demo.type === 'claimed' && (
              <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Lead Claimed!
                </h3>
                <div className="bg-white p-4 rounded-xl border border-slate-200">
                  <p className="font-semibold text-[#0F172A] mb-3">{currentStepData.demo.data.name}</p>
                  <div className="space-y-2">
                    <a href="#" className="flex items-center gap-2 text-[#0EA5E9] font-medium">
                      <Phone className="w-4 h-4" />
                      {currentStepData.demo.data.phone}
                    </a>
                    <a href="#" className="flex items-center gap-2 text-[#0EA5E9]">
                      <Mail className="w-4 h-4" />
                      {currentStepData.demo.data.email}
                    </a>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <span className="bg-[#0EA5E9]/10 text-[#0EA5E9] px-3 py-1 rounded-full text-sm">
                      {currentStepData.demo.data.service}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {currentStepData.demo.type === 'success' && (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-[#0F172A] mb-2">You Got the Job!</h3>
                <p className="text-slate-500 mb-6">Great work. Keep claiming leads to grow your business.</p>
                <a 
                  href="/contractors/portal"
                  className="inline-flex items-center gap-2 bg-[#F97316] text-white px-6 py-3 rounded-full font-bold hover:bg-[#EA580C]"
                >
                  Start Claiming Leads
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-12 bg-[#0F172A] rounded-2xl p-8 text-white">
          <h3 className="text-xl font-bold mb-6">Ready to Get Started?</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <a 
              href="/contractors/portal"
              className="bg-white/10 hover:bg-white/20 p-4 rounded-xl text-center transition-colors"
            >
              <p className="font-bold">Register Now</p>
              <p className="text-sm text-slate-300">Create your contractor account</p>
            </a>
            <a 
              href="/contractors/portal"
              className="bg-white/10 hover:bg-white/20 p-4 rounded-xl text-center transition-colors"
            >
              <p className="font-bold">Log In</p>
              <p className="text-sm text-slate-300">Access your dashboard</p>
            </a>
            <a 
              href="/"
              className="bg-white/10 hover:bg-white/20 p-4 rounded-xl text-center transition-colors"
            >
              <p className="font-bold">View Customer Site</p>
              <p className="text-sm text-slate-300">See what homeowners see</p>
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DemoWalkthrough;
